import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Building2, CalendarCheck, CalendarDays, Clock3, CreditCard, FileText, Fingerprint, Gauge, HelpCircle, Lock, ShieldCheck, UserCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const LINKS = [
  { dest: "/people#home-approvals", icon: UserCheck, title: "Approvals Center", desc: "Leave, timesheets, travel, letters, attendance corrections, field duty & OT — one inbox" },
  { dest: "/people#attendance", icon: Fingerprint, title: "Attendance", desc: "Punches, who is in today, geo-tagged check-ins" },
  { dest: "/people#leave", icon: CalendarDays, title: "Leave Tracker", desc: "Balances, applications, holidays" },
  { dest: "/people#payroll", icon: CreditCard, title: "Payroll", desc: "Salary slips and payroll runs" },
  { dest: "/people#reports", icon: Gauge, title: "Reports", desc: "Leave balance, early/late, presence hours, CSV export" },
  { dest: "/people#letters", icon: FileText, title: "HR Letters", desc: "Appointment, experience, relieving letters (PDF)" },
  { dest: "/people#org-policies", icon: ShieldCheck, title: "HR Policy", desc: "View policy, edit setup, approve & sync to ERPNext" },
  { dest: "/people#org-dataadmin", icon: Building2, title: "Data Administration", desc: "Departments, branches, biometric CSV punch import" },
  { dest: "/people#timetracker", icon: Clock3, title: "Time Tracker", desc: "Timers, time logs, timesheet review" },
  { dest: "/people#helpdesk", icon: HelpCircle, title: "HR Help Desk", desc: "Employee cases and FAQ" },
  { dest: "/people#org-employees", icon: CalendarCheck, title: "Employee Records", desc: "Edit profiles, status, linked documents" },
];

const ACCESS_MSG = "Your role is not specified for this section. Please contact HR.";
const destModule = (dest) => {
  const base = (dest.split("#")[1] || "home").split("-")[0];
  return base === "" ? "home" : base;
};

export const AdminPeople = ({ user, onAuthFail }) => {
  const [busy, setBusy] = useState("");
  const [modules, setModules] = useState(null);
  useEffect(() => {
    if (user?.role === "admin") return;
    api.get("/module-access/me").then(({ data }) => setModules(data.portal_modules)).catch(() => {});
  }, [user?.role, user?.email]);
  const isLocked = (dest) => Array.isArray(modules) && !modules.includes(destModule(dest));
  const openPortal = async (dest, key) => {
    if (isLocked(dest)) {
      toast.error(ACCESS_MSG);
      return;
    }
    setBusy(key);
    try {
      const { data } = await api.post("/auth/erp-sso-link", { dest });
      window.open(data.url, "_blank", "noopener");
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      else toast.error(err?.response?.data?.detail || "Could not open the People portal");
    } finally {
      setBusy("");
    }
  };
  return (
    <div data-testid="admin-people-hub">
      <div className="border border-nx-gold/30 bg-nx-gold/[0.04] p-6 sm:p-8 mb-6">
        <p className="text-[10px] tracking-[0.26em] uppercase text-nx-goldlight">One login · one place</p>
        <h2 className="font-marcellus text-3xl text-white mt-2">HR &amp; Payroll runs in the People portal</h2>
        <p className="text-sm text-slate-400 mt-3 max-w-2xl leading-relaxed">
          All HR and payroll work — leave, attendance, payroll, letters, policies, approvals — now lives in the NexSecure People portal
          at erp.nexsecure.in/people. Every button below signs you in automatically; no second password needed.
        </p>
        <button
          onClick={() => openPortal("/people", "main")}
          disabled={!!busy}
          className="mt-6 inline-flex items-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-6 py-3.5 transition-colors disabled:opacity-60"
          data-testid="people-open-portal"
        >
          {busy === "main" ? "Opening…" : "Open People Portal"} <ArrowUpRight size={14} />
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {LINKS.map((l) => {
          const Icon = l.icon;
          const locked = isLocked(l.dest);
          return (
            <button
              key={l.dest}
              onClick={() => openPortal(l.dest, l.dest)}
              disabled={!!busy}
              aria-disabled={locked}
              className={`text-left border border-white/10 bg-white/[0.02] p-5 transition-colors group disabled:opacity-60 ${locked ? "opacity-40 cursor-not-allowed" : "hover:border-nx-gold/50"}`}
              data-testid={`people-link-${l.dest.replace("/people#", "").replace("/people", "home")}`}
            >
              <div className="flex items-center justify-between">
                <Icon size={17} className={locked ? "text-slate-500" : "text-nx-goldlight"} />
                {locked ? <Lock size={13} className="text-slate-500" /> : <ArrowUpRight size={13} className="text-slate-600 group-hover:text-nx-goldlight transition-colors" />}
              </div>
              <p className="text-white text-sm mt-3">{l.title}</p>
              <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{l.desc}</p>
              {busy === l.dest && <p className="text-[10px] tracking-[0.16em] uppercase text-nx-goldlight mt-2">Opening…</p>}
            </button>
          );
        })}
      </div>
      <p className="mt-8 text-xs text-slate-600">Job applications &amp; candidate onboarding stay under Career Apps — that pipeline starts from the public website.</p>
    </div>
  );
};
